import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Capture scroll position before any DOM changes
    this.#previousScrollY = window.scrollY;

    requestAnimationFrame(() => {
      /*
       * Lock scroll using overflow:hidden on <html>.
       * This prevents the page from scrolling while the dialog is open
       * WITHOUT changing window.scrollY — the page stays at the same
       * position.  When the lock is removed on close, no scroll restoration
       * is required because the position was never altered.
       *
       * This replaces the old body { position:fixed; top:-${scrollY}px }
       * approach, which set window.scrollY to 0 and required a fragile
       * timed scrollTo() call on close.  Safari (iOS and macOS) would
       * queue its own scroll-to-0 asynchronously when position:fixed was
       * removed from body, racing and winning against the scrollTo().
       *
       * Scrollbar-width compensation: measure before adding overflow:hidden
       * so we can pad the body to prevent layout shift on desktop.
       */
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflowX = 'hidden';

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    const savedScrollY = this.#previousScrollY;

    dialog.close();
    dialog.classList.remove('dialog-closing');

    /*
     * Remove the scroll lock and dispatch the close event in a rAF so
     * focus-restoration side-effects from dialog.close() complete first.
     * window.scrollY was preserved throughout (overflow:hidden never
     * changes the scroll offset), so scrollTo is a no-op safety net.
     * DialogCloseEvent carries savedScrollY so cart-drawer.js
     * #handleHistoryClose has the authoritative value without re-reading
     * window.scrollY.
     */
    requestAnimationFrame(() => {
      document.body.style.paddingRight = '';
      document.body.style.overflowX = '';
      document.documentElement.style.overflow = '';
      window.scrollTo({ top: savedScrollY, behavior: 'instant' });
      this.dispatchEvent(new DialogCloseEvent(savedScrollY));
    });
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor(scrollY = 0) {
    super(DialogCloseEvent.eventName, { detail: { scrollY } });
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
